// Package Builder — content + placeholder substitution.
//
// Strings here are portable to Contentful — when the registration sweep
// happens, this object becomes the Contentful entry payloads. See
// `docs/features/2026.07.06-senior-market/2026.05.20-001-contentful-sweep-manifest.md` for the
// per-key mapping. `##PlaceholderName##` markers inside templates get
// substituted at render time via `substitute()`.
//
// Placeholders for this feature are all *modal-scoped* (current bundle name,
// live counts) — they do not belong on `mainStore.personalizationMap`, which
// is reserved for subject-derived identity data. Components compute the
// substitution vars from `packageBuilderStore` directly at render time.

import { makeContentModuleGetter } from '@/composables/contentful'

import type { ContentfulContentModule } from '#contentful/ContentfulContentModule'

export const builderContent = {
  // Dedicated step chrome. The step's `StorefrontHeader` takes its title from
  // the commerce step's `longName`; only the description is authored here.
  stepDescription: 'Build your own package by choosing the quantities of each sized sheet below.',
  // Link in the selection card header that drops the bundle from the cart.
  removeFromCartLabel: 'Remove from Cart',

  // Modal chrome. `modalHeading` / `instructions` are shared with the step,
  // where they head the selection card rather than the modal.
  modalHeading: 'Select Your Sheets',
  closeAriaLabel: 'Close',
  instructions: '##PackageTargetCount## Sheets',

  // Per-sheet stepper accessibility labels. The group + clear labels carry a
  // `##SheetName##` placeholder substituted with the sheet's display name at
  // render time (per-card runtime data, like the other modal-scoped vars).
  stepperGroupAriaLabelTemplate: 'Quantity for ##SheetName##',
  stepperDecrementAriaLabel: 'Remove one',
  stepperIncrementAriaLabel: 'Add one',
  clearAriaLabelTemplate: 'Remove all ##SheetName##',

  // Countdown — `##PackageRemainingCount##` is rendered bold by
  // `PackageBuilderCountdown.vue` via a split-render of this template.
  countdownTemplateUnder: 'Choose ##PackageRemainingCount## more sheets',
  countdownTemplateComplete: 'Complete!',

  // Footer
  ctaLabel: 'Add to Cart',
  ctaSubmittingLabel: 'Adding…',
  cancelLabel: 'Cancel',
  errorMessage:
    "We couldn't add this package to your cart. Please try again or contact customer service.",

  // Post-add toast (fired from the footer after a successful add)
  toastSuccessTemplate: '"##PackageBuilderBundleName##" was added to your cart!'
}

export type BuilderContent = typeof builderContent

// Resolves all Package Builder copy from its Contentful content module,
// falling back to the hardcoded `builderContent` per field. Returns the static
// object verbatim when the module is absent (not yet loaded / fetch failed).
// Identifiers map 1:1 to the content-model guide `2026.06.08-004`.
export function resolveBuilderContent(module: ContentfulContentModule | null): BuilderContent {
  if (!module) {
    return builderContent
  }
  const get = makeContentModuleGetter(module)
  return {
    stepDescription: get('senior-package-builder-step-description', builderContent.stepDescription),
    removeFromCartLabel: get(
      'senior-package-builder-remove-from-cart-label',
      builderContent.removeFromCartLabel
    ),
    modalHeading: get('senior-package-builder-modal-heading', builderContent.modalHeading),
    closeAriaLabel: get('senior-package-builder-close-aria', builderContent.closeAriaLabel),
    instructions: get('senior-package-builder-instructions', builderContent.instructions),
    stepperGroupAriaLabelTemplate: get(
      'senior-package-builder-stepper-group-aria',
      builderContent.stepperGroupAriaLabelTemplate
    ),
    stepperDecrementAriaLabel: get(
      'senior-package-builder-stepper-decrement-aria',
      builderContent.stepperDecrementAriaLabel
    ),
    stepperIncrementAriaLabel: get(
      'senior-package-builder-stepper-increment-aria',
      builderContent.stepperIncrementAriaLabel
    ),
    clearAriaLabelTemplate: get(
      'senior-package-builder-clear-aria',
      builderContent.clearAriaLabelTemplate
    ),
    countdownTemplateUnder: get(
      'senior-package-builder-countdown-template-under',
      builderContent.countdownTemplateUnder
    ),
    countdownTemplateComplete: get(
      'senior-package-builder-countdown-template-complete',
      builderContent.countdownTemplateComplete
    ),
    ctaLabel: get('senior-package-builder-cta-label', builderContent.ctaLabel),
    ctaSubmittingLabel: get(
      'senior-package-builder-cta-submitting-label',
      builderContent.ctaSubmittingLabel
    ),
    cancelLabel: get('senior-package-builder-cancel-label', builderContent.cancelLabel),
    errorMessage: get('senior-package-builder-error-message', builderContent.errorMessage),
    toastSuccessTemplate: get(
      'senior-package-builder-toast-success-template',
      builderContent.toastSuccessTemplate
    )
  }
}

// Re-exported so consumers can keep importing `substitute` from this module;
// the implementation is shared across the Senior feature content modules.
export { substitute } from '@/composables/contentful'
